import React, { useState, useEffect } from 'react';
import { FaTimes, FaTag, FaSave, FaSpinner } from 'react-icons/fa';
import { CloseModal } from '../sites/closemodal';

interface DesignationFormData {
  name: string;
  status?: 'ACTIVE' | 'SUSPENDED';
  show_in_reporting_person_list: boolean;
}

interface AddDesignationOffcanvasProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: DesignationFormData) => void;
  loading?: boolean;
}

export const AddDesignationOffcanvas: React.FC<AddDesignationOffcanvasProps> = ({
  isOpen,
  onClose,
  onSubmit,
  loading = false
}) => {
  const [formData, setFormData] = useState<DesignationFormData>({
    name: '',
    status: 'ACTIVE',
    show_in_reporting_person_list: false
  });

  const [errors, setErrors] = useState<Partial<DesignationFormData>>({});
  const [touched, setTouched] = useState<Partial<Record<keyof DesignationFormData, boolean>>>({});
  const [showCloseModal, setShowCloseModal] = useState(false);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setFormData({
        name: '',
        status: 'ACTIVE',
        show_in_reporting_person_list: false
      });
      setErrors({});
      setTouched({});
    } else {
      setShowCloseModal(false);
    }
  }, [isOpen]);

  // Validation
  const validateField = (name: keyof DesignationFormData, value: string) => {
    switch (name) {
      case 'name':
        if (!value.trim()) {
          return 'Designation name is required';
        }
        if (value.trim().length < 2) {
          return 'Designation name must be at least 2 characters';
        }
        if (value.trim().length > 100) {
          return 'Designation name cannot exceed 100 characters';
        }
        return '';
      default:
        return '';
    }
  };

  const validateForm = (): boolean => {
    const newErrors: any = {};
    let isValid = true;

    // Validate all fields
    Object.keys(formData).forEach((key) => {
      const fieldKey = key as keyof DesignationFormData;
      if (fieldKey === 'name') {
        const error = validateField(fieldKey, formData[fieldKey] as string);
        if (error) {
          newErrors[fieldKey] = error;
          isValid = false;
        }
      }
    });

    setErrors(newErrors);
    return isValid;
  };

  // Handle input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    const fieldName = name as keyof DesignationFormData;

    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));

    // Clear error when user starts typing
    if (errors[fieldName]) {
      setErrors(prev => ({
        ...prev,
        [fieldName]: ''
      }));
    }

    // Mark field as touched
    setTouched(prev => ({
      ...prev,
      [fieldName]: true
    }));
  };

  // Handle blur events for validation
  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const fieldName = name as keyof DesignationFormData;

    setTouched(prev => ({
      ...prev,
      [fieldName]: true
    }));

    const error = validateField(fieldName, value);
    setErrors(prev => ({
      ...prev,
      [fieldName]: error
    }));
  };

  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Mark all fields as touched
    setTouched({
      name: true,
      status: true,
      show_in_reporting_person_list: true
    });

    if (validateForm()) {
      onSubmit(formData);
    }
  };

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen && !loading) {
        setShowCloseModal(true);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, loading, onClose]);

  const requestClose = () => {
    if (!loading) {
      setShowCloseModal(true);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-50 transition-opacity"
        onClick={requestClose}
      />

      {/* Offcanvas */}
      <div className="fixed top-0 right-0 h-full w-full max-w-2xl bg-white shadow-xl z-50 transform transition-transform overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <FaTag className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-800">Add New Designation</h2>
              <p className="text-sm text-gray-600">Create a new designation</p>
            </div>
          </div>
          <button
            onClick={requestClose}
            className="p-2 hover:bg-gray-200 rounded transition-colors"
            disabled={loading}
          >
            <FaTimes className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Designation Name */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                Designation Name *
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                onBlur={handleBlur}
                disabled={loading}
                className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                  errors.name && touched.name
                    ? 'border-red-300 bg-red-50'
                    : 'border-gray-300'
                } disabled:bg-gray-50 disabled:cursor-not-allowed`}
                placeholder="Enter designation name"
                maxLength={100}
              />
              {errors.name && touched.name && (
                <p className="mt-1 text-sm text-red-600">{errors.name}</p>
              )}
              <p className="mt-1 text-xs text-gray-500">
                {formData.name.length}/100 characters
              </p>
            </div>

            {/* Status */}
            <div>
              <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-2">
                Status
              </label>
              <select
                id="status"
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                disabled={loading}
                className="w-full px-3 py-2 border border-gray-300 rounded  focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors disabled:bg-gray-50 disabled:cursor-not-allowed"
              >
                <option value="ACTIVE">Active</option>
                <option value="SUSPENDED">Suspended</option>
              </select>
              <p className="mt-1 text-xs text-gray-500">
                Set the initial status for this designation
              </p>
            </div>

            <div>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.show_in_reporting_person_list}
                  onChange={(e) => {
                    setFormData(prev => ({
                      ...prev,
                      show_in_reporting_person_list: e.target.checked
                    }));
                    setTouched(prev => ({
                      ...prev,
                      show_in_reporting_person_list: true
                    }));
                  }}
                  disabled={loading}
                  className="h-4 w-4 rounded border-gray-300 text-blue-700 focus:ring-blue-500"
                />
                <span className="text-sm font-medium text-gray-700">
                  Allow in Reporting Person List
                </span>
              </label>
            </div>

            {/* Info Box */}
            <div className="bg-blue-50 border border-blue-200 rounded p-4">
              <div className="flex items-start gap-3">
                <FaTag className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-blue-800">
                  <p className="font-medium mb-1">Designation Guidelines</p>
                  <ul className="text-xs space-y-1 list-disc list-inside">
                    <li>Choose a clear, descriptive name</li>
                    <li>Keep it professional and appropriate</li>
                    <li>Avoid special characters or numbers</li>
                    <li>Make it unique within your organization</li>
                  </ul>
                </div>
              </div>
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="border-t bg-gray-50 px-6 py-4">
          <div className="flex flex-col sm:flex-row gap-3 sm:justify-end">
            <button
              type="button"
              onClick={requestClose}
              disabled={loading}
              className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded  hover:bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="submit"
              onClick={handleSubmit}
              disabled={loading || !formData.name.trim()}
              className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-white bg-blue-800 border border-transparent rounded hover:bg-blue-950 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <FaSpinner className="w-4 h-4 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <FaSave className="w-4 h-4" />
                  Create Designation
                </>
              )}
            </button>
          </div>
        </div>
      </div>
      <CloseModal
        isOpen={showCloseModal}
        onClose={() => setShowCloseModal(false)}
        onConfirm={() => {
          setShowCloseModal(false);
          onClose();
        }}
      />
    </>
  );
};
